import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import "./A3LandingPage.css";
import iconPlus from "../../Images/IconPlus.svg";
import iconMinus from "../../Images/IconMinus.svg";

/* ─── ALERT CARD ─── */
const AlertCard = ({ entry }) => (
  <div className="a3lp-alert">
    <span dangerouslySetInnerHTML={{ __html: entry.data }} />
  </div>
);

/* ─── DETAILS CARD ─── */
const DetailsCard = ({ entry }) => {
  let details = {};
  try {
    details = typeof entry.data === "string" ? JSON.parse(entry.data) : entry.data;
  } catch {
    details = {};
  }
  const keys = Object.keys(details);
  const mid = Math.ceil(keys.length / 2);
  const leftKeys = keys.slice(0, mid);
  const rightKeys = keys.slice(mid);

  return (
    <div className="a3lp-details-card">
      <div className="a3lp-details-heading">{entry.heading}:</div>
      <div className="a3lp-details-grid">
        {leftKeys.map((key, i) => (
          <React.Fragment key={i}>
            <span className="a3lp-details-key">{key}</span>
            <span className="a3lp-details-value" dangerouslySetInnerHTML={{ __html: details[key] || "NA" }} />
            {rightKeys[i] ? (
              <>
                <span className="a3lp-details-key">{rightKeys[i]}</span>
                <span className="a3lp-details-value" dangerouslySetInnerHTML={{ __html: details[rightKeys[i]] || "NA" }} />
              </>
            ) : (
              <>
                <span /><span />
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ─── USER CARD ─── */
const formatDateTime = (value) => {
  if (!value) return "Not available";

  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatBaseTaggingDate = (value) => {
  if (!value) return "Not available";

  const originalValue = String(value);
  const stringValue = originalValue.trim();

  try {
    const backendDateMatch = stringValue.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );

    if (!backendDateMatch) return originalValue;

    const day = Number(backendDateMatch[1]);
    const month = Number(backendDateMatch[2]);
    const year = Number(backendDateMatch[3]);
    const hour = Number(backendDateMatch[4]);
    const minute = Number(backendDateMatch[5]);
    const second = Number(backendDateMatch[6] || 0);
    const date = new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    );

    const isValidDate = !Number.isNaN(date.getTime())
      && date.getFullYear() === year
      && date.getMonth() === month - 1
      && date.getDate() === day
      && date.getHours() === hour
      && date.getMinutes() === minute
      && date.getSeconds() === second;

    if (!isValidDate) return originalValue;

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const displayHours = hour % 12 || 12;
    const meridiem = hour >= 12 ? "pm" : "am";

    return `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${date.getFullYear()}, ${displayHours}:${String(date.getMinutes()).padStart(2, "0")} ${meridiem}`;
  } catch {
    return originalValue;
  }
};

const getTaggingLabel = (callLogs) => {
  try {
    const tags = typeof callLogs === "string" ? JSON.parse(callLogs) : callLogs;
    return [tags?.type, tags?.area, tags?.subArea, tags?.resolutionCode].filter(Boolean).join(" · ");
  } catch {
    return "Recommended tagging";
  }
};

const hasOpenItems = (...values) => values.some((value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return value > 0;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return ["yes", "open", "true"].includes(value.trim().toLowerCase()) || Number(value) > 0;
  }
  return false;
});

const CallTypesBox = ({ title, entries }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="a3lp-call-types-box">
      <div className={`a3lp-call-types-header ${isOpen ? "is-open" : ""}`}>
        <span className="a3lp-call-types-title">{title || "LAST 5 CALL TYPES"}</span>
        <div className="a3lp-rolling-control">
         <span className="a3lp-rolling-btn">Date And Time</span>
          <button
            type="button"
            className="a3lp-rolling-toggle"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Collapse" : "Expand"} rolling history`}
            onClick={() => setIsOpen((current) => !current)}
          >
            <img src={isOpen ? iconMinus : iconPlus} alt="" />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="a3lp-call-types-list">
          {entries.slice(0, 5).map(([callType, date], idx) => (
            <div className="a3lp-call-type-item" key={idx}>
              <span className="a3lp-blue-dot" />
              <span
                className="a3lp-call-type-text"
                dangerouslySetInnerHTML={{ __html: callType || "" }}
              />
              <span className="a3lp-call-type-date">{date}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatAuditDate = (value) => {
  if (!value) return "—";

  const match = String(value).trim().match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return value;

  const [, month, day, year, hour, minute, second = "0"] = match;
  const date = new Date(+year, +month - 1, +day, +hour, +minute, +second);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== +year ||
    date.getMonth() !== +month - 1 ||
    date.getDate() !== +day
  ) {
    return value;
  }

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const displayHour = date.getHours() % 12 || 12;
  const meridiem = date.getHours() >= 12 ? "pm" : "am";
  return `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${date.getFullYear()}, ${displayHour}:${String(date.getMinutes()).padStart(2, "0")} ${meridiem}`;
};

const formatAuditValue = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "—";

  const stringValue = String(value).trim();
  const match = stringValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i
  );
  if (!match) return stringValue;

  const [, day, month, year, rawHour, minute, second = "0", meridiem] = match;
  let hour = +rawHour;
  if (meridiem) {
    hour %= 12;
    if (meridiem.toUpperCase() === "PM") hour += 12;
  }

  const date = new Date(+year, +month - 1, +day, hour, +minute, +second);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== +year ||
    date.getMonth() !== +month - 1 ||
    date.getDate() !== +day
  ) {
    return stringValue;
  }

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const displayHour = date.getHours() % 12 || 12;
  const displayMeridiem = date.getHours() >= 12 ? "pm" : "am";
  return `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${date.getFullYear()}, ${displayHour}:${String(date.getMinutes()).padStart(2, "0")} ${displayMeridiem}`;
};

const getAuditTimestamp = (value) => {
  const match = String(value || "").trim().match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) return 0;
  return new Date(+match[3], +match[1] - 1, +match[2], +match[4], +match[5], +(match[6] || 0)).getTime();
};

const WorkOrderAudit = ({ entries }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [sortConfig, setSortConfig] = useState({ column: "date", direction: "desc" });
  const tableScrollRef = useRef(null);
  const scrollDragRef = useRef(null);
  const [scrollbar, setScrollbar] = useState({ top: 0, height: 36 });
  const auditEntries = Array.isArray(entries)
    ? entries.map((audit, sourceIndex) => ({ ...audit, sourceIndex })).sort((a, b) => {
        const difference = getAuditTimestamp(a?.date) - getAuditTimestamp(b?.date);
        return sortConfig.direction === "asc" ? difference : -difference;
      })
    : [];
  const latest = Array.isArray(entries)
    ? [...entries].sort((a, b) => getAuditTimestamp(b?.date) - getAuditTimestamp(a?.date))[0]
    : undefined;

  const handleDateSort = () => {
    setSortConfig((current) => ({
      column: "date",
      direction: current.column === "date" && current.direction === "asc" ? "desc" : "asc",
    }));
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  };

  const updateScrollbar = useCallback(() => {
    const element = tableScrollRef.current;
    if (!element) return;

    const { clientHeight, scrollHeight, scrollTop } = element;
    const thumbHeight = scrollHeight > clientHeight
      ? Math.max(36, (clientHeight * clientHeight) / scrollHeight)
      : clientHeight;
    const availableTrack = Math.max(0, clientHeight - thumbHeight);
    const availableScroll = Math.max(0, scrollHeight - clientHeight);

    setScrollbar({
      height: thumbHeight,
      top: availableScroll > 0 ? (scrollTop / availableScroll) * availableTrack : 0,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const frame = window.requestAnimationFrame(updateScrollbar);
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateScrollbar)
      : null;
    if (tableScrollRef.current) observer?.observe(tableScrollRef.current);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [auditEntries.length, isOpen, updateScrollbar]);

  const handleThumbPointerDown = (event) => {
    const element = tableScrollRef.current;
    if (!element) return;

    scrollDragRef.current = {
      startY: event.clientY,
      startScrollTop: element.scrollTop,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handleThumbPointerMove = (event) => {
    const drag = scrollDragRef.current;
    const element = tableScrollRef.current;
    if (!drag || !element) return;

    const availableTrack = element.clientHeight - scrollbar.height;
    const availableScroll = element.scrollHeight - element.clientHeight;
    if (availableTrack <= 0 || availableScroll <= 0) return;

    element.scrollTop = drag.startScrollTop
      + ((event.clientY - drag.startY) / availableTrack) * availableScroll;
  };

  if (auditEntries.length === 0) return null;

  return (
    <section className="a3lp-wo-audit">
      <div className="a3lp-audit-toolbar">
        <div className="a3lp-audit-title-row">
          <strong>AUDIT TRAIL</strong>
          <span className="a3lp-audit-count">{auditEntries.length} event{auditEntries.length === 1 ? "" : "s"}</span>
          <span className="a3lp-audit-source-badge">Read-only · from Siebel</span>
        </div>
        <div className="a3lp-audit-toolbar-right">
          {/* <span>1–{auditEntries.length} of {auditEntries.length}</span> */}
          <button
            type="button"
            className="a3lp-audit-toggle"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((current) => !current)}
          >
            <span>{isOpen ? "Hide" : "Show"}</span>
            <span className="a3lp-audit-toggle-icon" aria-hidden="true">
              {isOpen ? <ChevronUp size={16} strokeWidth={2.5} /> : <ChevronDown size={16} strokeWidth={2.5} />}
            </span>
          </button>
        </div>
      </div>

      {/* <div className="a3lp-audit-period">Last 6 months <span aria-hidden="true">▾</span></div> */}
{/*
      {latest && (
        <p className="a3lp-audit-latest">
          Last change: <strong>{latest?.field || "Field"}</strong> changed {formatAuditValue(latest?.oldValue)}
          <span aria-hidden="true"> → </span>{formatAuditValue(latest?.newValue)}
          <span className="a3lp-audit-latest-date"> · {formatAuditDate(latest?.date)}</span>
        </p>
      )} */}

      {isOpen && (
        <div className="a3lp-audit-table-shell">
          <div ref={tableScrollRef} className="a3lp-audit-table-wrap" onScroll={updateScrollbar}>
            <table className="a3lp-audit-table">
              <thead>
                <tr>
                  <th className={sortConfig.column === "date" ? "is-sorted" : ""}>
                    <button type="button" className="a3lp-audit-sort" onClick={handleDateSort}>
                      Date &amp; time
                      <span aria-hidden="true">
                        {sortConfig.column === "date"
                          ? (sortConfig.direction === "asc" ? "↑" : "↓")
                          : "↕"}
                      </span>
                    </button>
                  </th>
                  <th>BusinessComponent</th>
                  <th>OldValue</th>
                  <th>NewValue</th>
                  <th>Operation2</th>
                  <th>EmployeeLogin</th>
                </tr>
              </thead>
              <tbody>
                {auditEntries.map((audit) => (
                  <tr key={`${audit?.recordId || "audit"}-${audit?.date || "no-date"}-${audit?.field || "no-field"}-${audit.sourceIndex}`}>
                    <td>{formatAuditDate(audit?.date)}</td>
                    <td>{audit?.businessComponent || "—"}</td>
                    <td className={audit?.oldValue ? "a3lp-audit-old-value" : ""}>{formatAuditValue(audit?.oldValue)}</td>
                    <td className="a3lp-audit-new-value">{formatAuditValue(audit?.newValue)}</td>
                    <td>{audit?.operation2 || "—"}</td>
                    <td>{audit?.employeeLogin || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="a3lp-audit-scrollbar" aria-hidden="true">
            <div
              className="a3lp-audit-scrollbar-thumb"
              style={{ height: scrollbar.height, transform: `translateY(${scrollbar.top}px)` }}
              onPointerDown={handleThumbPointerDown}
              onPointerMove={handleThumbPointerMove}
              onPointerUp={() => { scrollDragRef.current = null; }}
              onPointerCancel={() => { scrollDragRef.current = null; }}
            />
          </div>
        </div>
      )}

      {/* <div className="a3lp-audit-footer">Source: Siebel WO Audit Trail · read-only</div> */}
    </section>
  );
};

const WorkOrderCard = ({ workOrder, position, total, buttons, onAction, disableActions }) => {
  const [selectedResponse, setSelectedResponse] = useState(0);
  const responses = workOrder?.responses || [];
  const recommendation = responses[selectedResponse] || {};
  const hasIspLog = String(workOrder?.isp || "").toLowerCase().startsWith("yes");
  const callLoggingString = workOrder?.callLoggingString || ''
  const  callLoggingDetails = workOrder?.callLoggingDetails || []
  const callEntries = Array.isArray(callLoggingDetails) ? callLoggingDetails : [];

  useEffect(() => setSelectedResponse(0), [workOrder?.wo_number]);

  return (
    <article className="a3lp-work-order-card">
      <header className="a3lp-work-order-header">
        <div>
          <span className="a3lp-eyebrow">Work order {position} of {total}</span>
          <div className="a3lp-work-order-number">{workOrder?.wo_number || "Work order"}</div>
        </div>
         <div>
          <span className="a3lp-eyebrow">Work order Type</span>
          <div className="a3lp-work-order-type">{workOrder?.wo_type}</div>
        </div>
        <span className={`a3lp-wo-status status-${String(workOrder?.wo_status || "unknown").toLowerCase().replace(/\s+/g, "-")}`}>
          <span className="a3lp-status-dot" />
          {workOrder?.wo_status || "Status unavailable"}
        </span>
      </header>

      <div className="a3lp-work-order-body">
        <section className="a3lp-wo-facts">
          <div className="a3lp-fact a3lp-fact-wide">
            <span>Visit slot</span>
            <strong>
              {!workOrder?.slot_details?.slot_start && !workOrder?.slot_details?.slot_end ? (
                "Not available"
              ) : (
                <>
                  {formatDateTime(workOrder?.slot_details?.slot_start)}
                  <i>to</i>
                  {formatDateTime(workOrder?.slot_details?.slot_end)}
                </>
              )}
            </strong>
            {workOrder?.slot_details?.wo_timeline && (
              <em className="a3lp-timing-chip">{workOrder.slot_details.wo_timeline}</em>
            )}
          </div>
          <div className="a3lp-fact">
            <span>Customer type</span>
            <strong>{workOrder?.region_type || "Not available"}</strong>
          </div>
          <div className="a3lp-fact">
            {/* <span>Created on</span> */}
            <span>WO Created on</span>

            <strong>{formatDateTime(workOrder?.wo_created_date)}</strong>
          </div>
          <div className="a3lp-fact">
            {/* <span>Customer call</span> */}
            <span>Did customer call post WO creation</span>
            <strong>{workOrder?.customer_call_timing || "Not available"}</strong>
          </div>
          <div className="a3lp-fact">
            <span>Base tagging</span>
            <div className="a3lp-base-tag-row">
              <strong
                className="a3lp-base-tag"
                dangerouslySetInnerHTML={{ __html: workOrder?.base_tagging || "Not tagged" }}
              />
              {workOrder?.base_tagging_date && (
                <>
                  <span className="a3lp-base-tag-separator" aria-hidden="true">|</span>
                  <strong
                    className="a3lp-base-tag"
                  >
                    {formatBaseTaggingDate(workOrder.base_tagging_date)}
                  </strong>
                </>
              )}
            </div>
          </div>
        </section>

        <div className={`a3lp-isp-note ${hasIspLog ? "has-log" : "no-log"}`}>
          <span className="a3lp-isp-icon">{hasIspLog ? "✓" : "i"}</span>
          <div>
            <strong>ISP call log — {hasIspLog ? "Present" : "Not present"}</strong>
            <p
              dangerouslySetInnerHTML={{
                __html: `Call Log Description: ${workOrder?.isp_description || "No ISP or ASM log is available under this work order."}`,
              }}
            />
          </div>
        </div>
      </div>

      <WorkOrderAudit entries={workOrder?.wo_audit} />


      {callLoggingString && <CallTypesBox title={callLoggingString} entries={callEntries} />}
      {responses.length > 0 && (
        <section className="a3lp-guidance-panel">
          <div className="a3lp-guidance-topbar">
            <div>
              <span className="a3lp-eyebrow">Call Tagging</span>
              <strong>{getTaggingLabel(recommendation?.call_logs)}</strong>
            </div>
            {responses.length > 1 && (
              <div className="a3lp-response-switch" role="tablist" aria-label="Customer response">
                {responses.map((response, index) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={selectedResponse === index}
                    className={selectedResponse === index ? "is-active" : ""}
                    onClick={() => setSelectedResponse(index)}
                    key={`${response?.consent || "response"}-${index}`}
                  >
                    {response?.consent || `Option ${index + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          

          {(recommendation?.script1 || recommendation?.script2) && (
            <details className="a3lp-agent-script" open>
              <summary>Agent script <span>⌄</span></summary>
              <div className="a3lp-agent-script-content">
                {recommendation?.script1 && <p dangerouslySetInnerHTML={{ __html: recommendation.script1 }} />}
                {recommendation?.script2 && <p dangerouslySetInnerHTML={{ __html: recommendation.script2 }} />}
              </div>
            </details>
          )}

          <div className="a3lp-next-action">
            <span>→</span>
            <div>
              <strong>Next action</strong>
              <p
                dangerouslySetInnerHTML={{
                  __html: recommendation?.next_action || "Follow the recommended CRM workflow.",
                }}
              />
            </div>
          </div>

          {!disableActions && buttons.length > 0 && (
            <div className="a3lp-actions action-tab">
              {buttons.map((button, index) => (
                <button
                  type="button"
                  onClick={() => onAction(button, recommendation?.consent)}
                  key={`${button?.opcode || "action"}-${index}`}
                >
                  {button?.buttonName || "Continue"}
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </article>
  );
};

const UserCard = ({ entry, addMessages, openWorkflow, disableActions, disableServiceRequest }) => {
  let profile = {};
  try {
    profile = typeof entry.data === "string" ? JSON.parse(entry.data) : entry.data;
  } catch {
    profile = {};
  }
  const hasSummary = profile?.summary !== undefined && profile?.summary !== null;
  const {
    summary = {},
    isPredictiveEscalationDeskCallTaggingExists,
    callLoggingString,
    callLoggingDetails,
  } = profile;

  const callEntries = Array.isArray(callLoggingDetails) ? callLoggingDetails : [];
  const response = summary?.response || {};
  const workOrders = response?.work_orders || [];
  const buttons = summary?.buttons || [];
  const hasOpenWorkOrder = hasOpenItems(
    workOrders,
    profile?.woQueryList,
    response?.work_order_count,
    response?.open_work_order
  );
  const hasOpenServiceRequest = hasOpenItems(
    profile?.srDetails,
    response?.service_requests,
    response?.service_request_count,
    response?.open_service_request
  );
  const [activeWorkOrder, setActiveWorkOrder] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleAction = (button, selectedConsent) => {
    const selectedBoxNumber = sessionStorage.getItem("selectedBoxNumber") || "";
    const buttonName = button?.ctaName || button?.buttonName || "Continue";
    const consent = selectedConsent || "Agrees";

    if (button?.isQuickTab && openWorkflow) {
      openWorkflow(
        buttonName,
        button?.opcode,
        button?.mappedId,
        null,
        selectedBoxNumber,
        consent
      );
      return;
    }

    addMessages?.(selectedBoxNumber, button?.opcode, consent, buttonName);
  };

  return (
    <div className="a3lp-user-card">
      <h3 className="a3lp-details-heading a3lp-user-profile-title">
        {entry.heading || "User Profile"}
      </h3>

      <div className="a3lp-open-status-grid">
        <div className={`a3lp-open-status-card ${hasOpenWorkOrder ? "is-open" : "is-closed"}`}>
          <div className="a3lp-open-status-heading">
            <span>Open Work Order</span>
            {hasSummary && (
              <button
                type="button"
                className="a3lp-summary-toggle"
                aria-label={isCollapsed ? "Expand work-order summary" : "Collapse work-order summary"}
                aria-expanded={!isCollapsed}
                onClick={() => setIsCollapsed((current) => !current)}
              >
                <img src={isCollapsed ? iconPlus : iconMinus} alt="" />
              </button>
            )}
          </div>
          <strong><i />{hasOpenWorkOrder ? "Yes" : "No"}</strong>
        </div>

        {!disableServiceRequest && (
          <div className={`a3lp-open-status-card ${hasOpenServiceRequest ? "is-open" : "is-closed"}`}>
            <div className="a3lp-open-status-heading">
              <span>Open Service Request</span>
            </div>
            <strong><i />{hasOpenServiceRequest ? "Yes" : "No"}</strong>
          </div>
        )}
      </div>

      

      {(!hasSummary || !isCollapsed) && (
        <>
          {/* <div className="a3lp-user-card-heading">
            <div>
              <span className="a3lp-eyebrow">Agent assist</span>
              <h3>{entry.heading || "Customer work orders"}</h3>
            </div>
            <div className="a3lp-profile-summary">
              {response?.subscriber_id && <span>Subscriber <strong>{response.subscriber_id}</strong></span>}
              <span>{workOrders.length} open work order{workOrders.length === 1 ? "" : "s"}</span>
            </div>
          </div> */}

          {isPredictiveEscalationDeskCallTaggingExists && (
            <div className="a3lp-predictive-box">
              <div className="a3lp-predictive-header">
                <span>♡ HANDLE WITH CARE</span>
                <button className="a3lp-predictive-btn">Predictive Flag</button>
              </div>
              <div className="a3lp-predictive-body">
                <span className="a3lp-warning-icon">⚠️</span>
                <div>
                  <strong>This subscriber has history of Using Predictive Key words.</strong>
                  <div className="a3lp-predictive-sub">Engage with empathy · Escalate if required</div>
                </div>
              </div>
            </div>
          )}

          {workOrders.length > 1 && (
            <div className="a3lp-work-order-tabs" role="tablist" aria-label="Open work orders">
              {workOrders.map((workOrder, index) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeWorkOrder === index}
                  className={activeWorkOrder === index ? "is-active" : ""}
                  onClick={() => setActiveWorkOrder(index)}
                  key={workOrder?.wo_number || index}
                >
                  <span>{workOrder?.wo_number || `Order ${index + 1}`}</span>
                  <small>{workOrder?.wo_status || "Unknown"}</small>
                </button>
              ))}
            </div>
          )}

          {workOrders.length > 0 ? (
            <WorkOrderCard
              workOrder={workOrders[activeWorkOrder]}
              position={activeWorkOrder + 1}
              total={workOrders.length}
              buttons={buttons}
              onAction={handleAction}
              disableActions={disableActions}
            />
          ) : (
            <div className="a3lp-empty-work-orders">No open work orders found for this subscriber.</div>
          )}
        </>
      )}
      {callLoggingString && <CallTypesBox title={callLoggingString} entries={callEntries} />}
    </div>
  );
};

/* ─── COMPONENT MAP ─── */
const COMPONENT_MAP = {
  alert: AlertCard,
  details_card: DetailsCard,
  user_card: UserCard,
};

/* ─── MAIN LANDING PAGE ─── */
const A3LandingPage = ({ item, addMessages, openWorkflow, disableServiceRequest = false }) => {
  let data = [];
  try {
    const raw = item?.text || item?.data;
    data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(data)) data = [];
  } catch {
    data = [];
  }

  let metadata = {};
  try {
    metadata = typeof item?.data === "string" ? JSON.parse(item.data) : item?.data || {};
  } catch {
    metadata = {};
  }
  const disableButton = metadata?.qt_disable === "yes";

  return (
    <div className="a3lp-container">
      {data.map((entry, idx) => {
        const Component = COMPONENT_MAP[entry.uiType];
        if (!Component) return null;
        return (
          <Component
            key={idx}
            entry={entry}
            addMessages={addMessages}
            openWorkflow={openWorkflow}
            disableActions={disableButton}
            disableServiceRequest={disableServiceRequest}
          />
        );
      })}
    </div>
  );
};

export default A3LandingPage;
